import PropTypes from 'prop-types';
import { useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import Heading from 'shopper/components/Heading';
import Text from 'shopper/components/Text';

import placeholder from 'lib/placeholder';
import { noop } from 'lib/utils';

import Star from './Star';

const RATING_STARS_TO_SHOW = 5;
const RATING_STARS = [1, 2, 3, 4, 5];

const parseRating = (rating) => rating.toString().replace('.', ',');

const StarsRating = ({
  ariaLabelWhenRated = '',
  className,
  clickable = true,
  rating = 0,
  showFeedbackText = true,
  size = 'size1',
  topLabel = null,
  userStarRating = null,
  withRatingAverage = false,
  onRatingClick = noop,
}) => {
  const [isRated, setRated] = useState(false);
  const [isRatingHovered, setRatingHovered] = useState(false);
  const [starBeingHoveredPosition, setStarBeingHoveredPosition] = useState(0);

  const staredWithRatingOrUserStar = isRated ? userStarRating : rating;

  const onRatingClickHandler = () => {
    onRatingClick(starBeingHoveredPosition);
    setRated(true);
  };

  const onHoverStar = (position) => {
    if (!clickable) {
      return;
    }

    setStarBeingHoveredPosition(position);
  };

  return (
    <div
      className={twMerge(
        'flex flex-col items-start justify-between md:justify-center',
        className
      )}
    >
      {!isRated && topLabel && <Text>{topLabel}</Text>}
      <div
        className={twJoin(
          'flex gap-x-1',
          topLabel && 'mt-2 gap-x-2 md:min-w-52'
        )}
        onMouseEnter={() => {
          if (clickable) {
            setRatingHovered(true);
          }
        }}
        onMouseLeave={() => {
          if (clickable) {
            setRatingHovered(false);
            setStarBeingHoveredPosition(0);
          }
        }}
      >
        {(!isRated || !showFeedbackText) &&
          RATING_STARS.map((position) => {
            const isStaredWithRatingOrUserStar =
              (!isRatingHovered &&
                Math.round(staredWithRatingOrUserStar) >= position) ||
              starBeingHoveredPosition >= position;

            return (
              <Star
                key={position}
                aria-label="Atribuir nota"
                ariaLabelWhenRated={ariaLabelWhenRated}
                clickable={clickable}
                size={size}
                stared={isStaredWithRatingOrUserStar}
                onClick={onRatingClickHandler}
                onMouseEnter={() => {
                  onHoverStar(position);
                }}
              />
            );
          })}
        {isRated && showFeedbackText && (
          <Text bold>{placeholder('descriptions.ratingDone')}</Text>
        )}
        {withRatingAverage && !isRated && (
          <div className="ml-1 flex items-end">
            <Heading as="span" size="size6">
              {parseRating(rating)}
            </Heading>
            <Text as="span" size="size2">{`/${RATING_STARS_TO_SHOW}`}</Text>
          </div>
        )}
      </div>
    </div>
  );
};

StarsRating.propTypes = {
  ariaLabelWhenRated: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  clickable: PropTypes.bool,
  showFeedbackText: PropTypes.bool,
  size: PropTypes.string,
  rating: PropTypes.number,
  topLabel: PropTypes.string,
  userStarRating: PropTypes.number,
  withRatingAverage: PropTypes.bool,
  onRatingClick: PropTypes.func,
};

export default StarsRating;
